import { db } from './db';
import { logger } from '../utils/logger';
import { useLiveQuery } from 'dexie-react-hooks';
import { selectWindows } from '../store/windowsSlice';
import { useAppSelector } from '../store/hooks';

interface ThrottleState {
  source: string;
  type: string;
  current: number;
  total: number;
  lastUpdate: number;
  timeout: ReturnType<typeof setTimeout> | null;
}

const throttleMap = new Map<string, ThrottleState>();

export async function reportProgress(
  id: string,
  source: string,
  type: string,
  current: number,
  total: number
): Promise<void> {
  const THROTTLE_MS = 100; // Throttle to 10fps for smooth UI without database lockups
  const now = Date.now();
  let state = throttleMap.get(id);

  if (!state) {
    state = { source, type, current, total, lastUpdate: 0, timeout: null };
    throttleMap.set(id, state);
  }

  state.source = source;
  state.type = type;
  state.current = current;
  state.total = total;

  if (now - state.lastUpdate >= THROTTLE_MS || current === total) {
    if (state.timeout) {
      clearTimeout(state.timeout);
      state.timeout = null;
    }
    state.lastUpdate = now;
    // We await the actual DB put so that some calls apply backpressure
    await db.progress.put({
      id,
      source: state.source,
      type: state.type,
      current: state.current,
      total: state.total,
      updatedAt: now,
    });
    return;
  }

  if (!state.timeout) {
    state.timeout = setTimeout(() => {
      const currentState = throttleMap.get(id);
      if (currentState) {
        currentState.lastUpdate = Date.now();
        currentState.timeout = null;
        // Fire and forget, don't block the setTimeout
        db.progress.put({
          id,
          source: currentState.source,
          type: currentState.type,
          current: currentState.current,
          total: currentState.total,
          updatedAt: currentState.lastUpdate,
        }).catch(err => logger.error("Delayed progress put failed", err));
      }
    }, THROTTLE_MS - (now - state.lastUpdate));
  }

  // Resolve immediately so the tight loops don't block waiting for the throttle delay
  return Promise.resolve();
}

export async function clearProgress(id: string, completed: boolean = false): Promise<void> {
  const state = throttleMap.get(id);
  if (state && state.timeout) {
    clearTimeout(state.timeout);
  }
  if (completed && state) {
    try {
      await db.completedProgress.add({
        originalId: id,
        source: state.source,
        type: state.type,
        total: state.total,
        completedAt: Date.now(),
      });
      // Keep only the 10 most recent
      const count = await db.completedProgress.count();
      if (count > 10) {
        const oldest = await db.completedProgress.orderBy('completedAt').first();
        if (oldest && oldest.id) {
          await db.completedProgress.delete(oldest.id);
        }
      }
    } catch (e) {
      logger.error('Failed to log completed progress', e);
    }
  }
  throttleMap.delete(id);
  await db.progress.delete(id);
}

export async function clearAllProgress(): Promise<void> {
    for (const state of throttleMap.values()) {
      if (state.timeout) {
        clearTimeout(state.timeout);
      }
    }
    throttleMap.clear();
    await db.progress.clear();
}

export interface ProgressItem {
    id?: string;
    source: string;
    type: string;
    current: number;
    total: number;
    updatedAt: number;
}

export interface ProgressSummary {
    totalWork: number;
    completedWork: number;
    activeTasks: number;
    operations: ProgressItem[];
}

// If a progress entry hasn't been updated in this long, it's from a killed
// service worker session and should not appear as "still running" in the UI.
const STALE_PROGRESS_MS = 30_000;

export function useActiveProgress(): ProgressSummary {
    const windows = useAppSelector(selectWindows);
    const progressItems = useLiveQuery(() => db.progress.toArray()) || [];
    const now = Date.now();

    // Filter items — backup has its own UI indicator, exclude it from the spinner.
    // Also filter stale entries (updatedAt > 30s ago) that are orphaned from a
    // killed service worker session and were never cleaned up.
    const activeItems = progressItems.filter(item => {
        if (item.type === 'backup') return false;
        if (now - item.updatedAt > STALE_PROGRESS_MS) return false;
        if (item.source === 'background') return true;
        if (item.source.startsWith('window-')) {
            const windowIdStr = item.source.replace('window-', '');
            const windowId = parseInt(windowIdStr);
            return windows[windowId] !== undefined;
        }
        return false;
    });

    const summary = activeItems.reduce((acc, item) => {
        return {
            totalWork: acc.totalWork + item.total,
            completedWork: acc.completedWork + item.current,
            activeTasks: acc.activeTasks + 1,
            operations: [...acc.operations, item]
        };
    }, { totalWork: 0, completedWork: 0, activeTasks: 0, operations: [] as ProgressItem[] });

    return summary;
}

export function useBackgroundProgress(): ProgressSummary {
    const progressItems = useLiveQuery(() => db.progress.where('source').equals('background').toArray()) || [];
    const now = Date.now();

    const summary = progressItems.filter(item => now - item.updatedAt <= STALE_PROGRESS_MS).reduce((acc, item) => {
        return {
            totalWork: acc.totalWork + item.total,
            completedWork: acc.completedWork + item.current,
            activeTasks: acc.activeTasks + 1,
            operations: [...acc.operations, item]
        };
    }, { totalWork: 0, completedWork: 0, activeTasks: 0, operations: [] as ProgressItem[] });

    return summary;
}

export interface CompletedProgressItem {
  id?: number;
  originalId: string;
  source: string;
  type: string;
  total: number;
  completedAt: number;
}

export function useCompletedProgress(): CompletedProgressItem[] {
  const completedItems = useLiveQuery(() => 
    db.completedProgress.orderBy('completedAt').reverse().limit(10).toArray()
  ) || [];
  return completedItems;
}
